/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { PlusIcon } from "lucide-react"
import type { ComponentProps } from "react"
import { useState } from "react"

import { cn } from "@/utils/cn"

import { BusyButton } from "../components/busy-button"

/**
 * `BusyButton` props except the state and handler owned by this component.
 * All remaining props, including the button label, are passed through.
 */
interface Props extends Omit<ComponentProps<typeof BusyButton>, "busy" | "onClick"> {
    /**
     * Loads the next page. The button remains busy until the promise settles.
     */
    onLoad: () => Promise<void>
}

/**
 * Appends the next page of a paginated list.
 *
 * Shows a spinner and prevents another load while `onLoad` is running. The
 * button stays focusable while busy so keyboard users keep their place in the
 * tab order; Base UI uses `aria-disabled` and suppresses activation instead of
 * removing the button from focus.
 *
 * Defaults to an outline button centered below the list.
 *
 * @example
 * <LoadMoreButton onLoad={() => vm.appendApplicationEvent()}>Load More</LoadMoreButton>
 */
export const LoadMoreButton = ({ onLoad, className, children, ...props }: Props) => {
    const [busy, setBusy] = useState(false)

    const load = async () => {
        setBusy(true)
        try {
            await onLoad()
        } finally {
            setBusy(false)
        }
    }

    return (
        <BusyButton
            variant="outline"
            focusableWhenDisabled
            {...props}
            className={cn("justify-self-center uppercase", className)}
            busy={busy}
            onClick={load}
        >
            {!busy && <PlusIcon data-icon="inline-start" />}
            {children}
        </BusyButton>
    )
}
